import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';

// Build tag emitted into the runtime so rollup mints a fresh App-* hash.
// Bumped 2026-05-10e+1 after CF Pages cached a transient 404 on the prior
// hash during a deploy race.
const __BUILD_TAG__ = '2026-05-10e+1';
if (typeof window !== 'undefined') (window as { __reloBuild?: string }).__reloBuild = __BUILD_TAG__;
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './lib/i18n';
import { ProtectedRoute, PartnerRoute, ClientPortalRoute } from './components/ProtectedRoute';
import { V2DefaultRedirect } from './components/V2DefaultRedirect';
import { V2RouteGuard } from './components/V2RouteGuard';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { colors } from './styles/theme';
import { queryClient } from './lib/queryClient';
import { V2ToastViewport } from './v2/components/primitives/Toast';

// V1 admin pages — chunk K5 deleted the 30 with V2 parity. Chunks V/W/X/Y
// retired the last out-of-tree V1 admin pages (MediaSources, Audiences,
// RuleBuilder, ControlCenter). NewDashboardLayout has zero consumers and
// was deleted in chunk Y. Redirects below preserve old bookmarks.

// v2 design-system app (separate component tree at /v2/*).
// Uses its own internal router (state-based, not URL). Loaded lazily so
// the legacy bundle isn't bloated. Wrapped in `.v2-root` so the design
// tokens (v2-brand, v2-surface-*, etc.) and base styles apply only here.
const V2App = lazy(() => import('./v2/App'));
// Auth pages
const LoginPage = lazy(() => import('./pages/auth').then(m => ({ default: m.LoginPage })));
const SignupPage = lazy(() => import('./pages/auth').then(m => ({ default: m.SignupPage })));
const ForgotPasswordPage = lazy(() => import('./pages/auth').then(m => ({ default: m.ForgotPasswordPage })));
const AuthCallbackPage = lazy(() => import('./pages/auth').then(m => ({ default: m.AuthCallbackPage })));
const ResetPasswordPage = lazy(() => import('./pages/auth').then(m => ({ default: m.ResetPasswordPage })));
const SetupPage = lazy(() => import('./pages/auth').then(m => ({ default: m.SetupPage })));

// Client Portal
const ClientPortalRedirect = lazy(() => import('./pages/client-portal/ClientPortalRedirect').then(m => ({ default: m.ClientPortalRedirect })));
const ClientPortalRouter = lazy(() => import('./pages/client-portal/ClientPortalRouter').then(m => ({ default: m.ClientPortalRouter })));
const ClientInviteAccept = lazy(() => import('./pages/client-portal/ClientInviteAccept').then(m => ({ default: m.ClientInviteAccept })));

// V1 → V2 path-rewriting helpers used by the K5 admin redirect block.
// Kept inline to avoid spinning up a one-line helper file.
function RedirectToV2Partner() {
  const { id } = useParams();
  return <Navigate to={`/v2/partners/${id ?? ''}`} replace />;
}

function RedirectClient({ suffix }: { suffix: string }) {
  const { clientSlug } = useParams();
  const tail = suffix ? `/${suffix}` : '';
  return <Navigate to={`/v2/c/${clientSlug ?? ''}${tail}`} replace />;
}

function RedirectClientCampaign() {
  const { clientSlug, campaignId } = useParams();
  return <Navigate to={`/v2/c/${clientSlug ?? ''}/campaigns/${campaignId ?? ''}`} replace />;
}

// Loading fallback component
function PageLoader() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        color: colors.textMuted,
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: '32px',
            height: '32px',
            border: `3px solid ${colors.borderDefault}`,
            borderTopColor: colors.brand,
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 12px',
          }}
        />
        <div style={{ fontSize: '14px' }}>Loading…</div>
      </div>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <LanguageProvider>
    <AuthProvider>
      {/*
        Single global toast surface for the whole app (V1 remnants — login,
        client portal — AND V2). Previously this file ALSO mounted its own
        `<Toaster position="top-right">` while `V2App` independently mounted
        `<V2ToastViewport>` (bottom-right). react-hot-toast keeps ONE global
        toast queue; every `<Toaster>` instance mounted anywhere renders
        EVERY toast in that queue, regardless of which one created it. With
        two Toasters alive at once, each toast (v2Toast.* AND the plain
        toast.* calls in src/lib/auth.ts / src/lib/queryClient.ts) rendered
        TWICE, once per Toaster, in two different positions/styles — the
        "long unreadable popup, then another stacked on top" bug on
        /v2/cambios (2026-07). Fixed by mounting exactly one Toaster, here,
        and removing the duplicate from v2/App.tsx.
      */}
      <V2ToastViewport />
      <BrowserRouter>
        <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ============================================ */}
            {/* Auth Routes (Public) */}
            {/* ============================================ */}
            <Route path="/auth/login" element={<LoginPage />} />
            <Route path="/auth/signup" element={<SignupPage />} />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/setup" element={<SetupPage />} />

            {/* ============================================ */}
            {/* V1 admin routes — chunk K5 retired the V1 pages with V2     */}
            {/* parity and now redirects each path to its V2 equivalent.    */}
            {/* The "Back to legacy" toggle in the V2 sidebar still exists  */}
            {/* but is a no-op for admins (no V1 to fall back to). The      */}
            {/* toggle remains useful for partners/client_users whose V1    */}
            {/* surfaces are still in the tree.                             */}
            {/* ============================================ */}

            <Route path="/" element={<ProtectedRoute requireAdmin><Navigate to="/v2" replace /></ProtectedRoute>} />
            <Route path="/partners" element={<ProtectedRoute requireAdmin><Navigate to="/v2/partners" replace /></ProtectedRoute>} />
            <Route path="/partners/onboard" element={<ProtectedRoute requireAdmin><Navigate to="/v2/partners/onboarding" replace /></ProtectedRoute>} />
            <Route path="/partners/:id" element={<ProtectedRoute requireAdmin><RedirectToV2Partner /></ProtectedRoute>} />
            <Route path="/partners/:id/manage" element={<ProtectedRoute requireAdmin><RedirectToV2Partner /></ProtectedRoute>} />
            <Route path="/clients/new" element={<ProtectedRoute requireAdmin><Navigate to="/v2/clients/new" replace /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute requireAdmin><Navigate to="/v2/settings" replace /></ProtectedRoute>} />
            <Route path="/tickets" element={<ProtectedRoute requireAdmin><Navigate to="/v2/tickets" replace /></ProtectedRoute>} />
            <Route path="/system/pipeline" element={<ProtectedRoute requireAdmin><Navigate to="/v2/pipelines" replace /></ProtectedRoute>} />
            <Route path="/asset-packs" element={<ProtectedRoute requireAdmin><Navigate to="/v2/assets" replace /></ProtectedRoute>} />
            <Route path="/audit" element={<ProtectedRoute requireAdmin><Navigate to="/v2" replace /></ProtectedRoute>} />

            {/* Client-scoped redirects */}
            <Route path="/c/:clientSlug/dashboard" element={<ProtectedRoute requireAdmin><RedirectClient suffix="" /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/campaigns" element={<ProtectedRoute requireAdmin><RedirectClient suffix="campaigns" /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/campaigns/:campaignId" element={<ProtectedRoute requireAdmin><RedirectClientCampaign /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/partners" element={<ProtectedRoute requireAdmin><Navigate to="/v2/partners" replace /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/partners/:id" element={<ProtectedRoute requireAdmin><RedirectToV2Partner /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/payments" element={<ProtectedRoute requireAdmin><Navigate to="/v2/payments" replace /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/config" element={<ProtectedRoute requireAdmin><RedirectClient suffix="settings" /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/alerts" element={<ProtectedRoute requireAdmin><RedirectClient suffix="alerts" /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/asset-packs" element={<ProtectedRoute requireAdmin><Navigate to="/v2/assets" replace /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/link-generator" element={<ProtectedRoute requireAdmin><Navigate to="/v2/link-generator" replace /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/audit" element={<ProtectedRoute requireAdmin><RedirectClient suffix="audit" /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/fraud-review" element={<ProtectedRoute requireAdmin><RedirectClient suffix="fraud-review" /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/sla" element={<ProtectedRoute requireAdmin><RedirectClient suffix="sla" /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/ai-assistant" element={<ProtectedRoute requireAdmin><Navigate to="/v2/ai" replace /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/planner" element={<ProtectedRoute requireAdmin><RedirectClient suffix="planner" /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/budget-tree" element={<ProtectedRoute requireAdmin><Navigate to="/v2/budget-engine-runs" replace /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/quality" element={<ProtectedRoute requireAdmin><RedirectClient suffix="quality" /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/import" element={<Navigate to="../config?tab=import" replace />} />
            <Route path="/c/:clientSlug/products" element={<Navigate to="../config?tab=products" replace />} />

            {/* ============================================ */}
            {/* V1 admin pages still alive (no V2 parity yet)              */}
            {/* ============================================ */}
            <Route path="/media-sources" element={<ProtectedRoute requireAdmin><Navigate to="/v2/c/samsung/media-sources" replace /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/media-sources" element={<ProtectedRoute requireAdmin><RedirectClient suffix="media-sources" /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/leads" element={<ProtectedRoute requireAdmin><RedirectClient suffix="leads" /></ProtectedRoute>} />
            <Route path="/c/:clientSlug/audiences" element={<ProtectedRoute requireAdmin><RedirectClient suffix="audiences" /></ProtectedRoute>} />

            {/* Visual rule builder — V1 page deleted; the V2 rule editor now
                lives inside the rules surface at /v2/c/:slug/rules. Old
                bookmark redirects to Samsung (V1 default clientId was 3). */}
            <Route path="/rules/new" element={<Navigate to="/v2/c/samsung/rules" replace />} />

            {/* Control Center — V1 page deleted, V2 lives at /v2/control-center. */}
            <Route path="/control-center" element={<Navigate to="/v2/control-center" replace />} />

            {/* Legacy redirect */}
            <Route path="/partners-manage" element={<Navigate to="/partners" replace />} />
            <Route path="/payments" element={<Navigate to="/" replace />} />
            <Route path="/products" element={<Navigate to="/" replace />} />
            <Route path="/import" element={<Navigate to="/" replace />} />

            {/* ============================================ */}
            {/* Client Portal Routes */}
            {/* /client/:slug - Client-facing dashboard */}
            {/* ============================================ */}

            {/* Client portal redirect - resolves client slug from auth */}
            <Route path="/client" element={
              <ClientPortalRoute>
                <Suspense fallback={<PageLoader />}>
                  <ClientPortalRedirect />
                </Suspense>
              </ClientPortalRoute>
            } />

            {/* Client invitation acceptance (public) */}
            <Route path="/client/invite" element={
              <Suspense fallback={<PageLoader />}>
                <ClientInviteAccept />
              </Suspense>
            } />

            {/* Client portal dashboard — Phase E cutover: client_users default
                to /v2/c/:slug/portal. Toggle off in v2 sidebar to fall back here. */}
            <Route path="/client/:clientSlug" element={
              <ClientPortalRoute>
                <V2DefaultRedirect>
                  <Suspense fallback={<PageLoader />}>
                    <ClientPortalRouter />
                  </Suspense>
                </V2DefaultRedirect>
              </ClientPortalRoute>
            } />

            {/* Client portal sub-routes */}
            <Route path="/client/:clientSlug/*" element={
              <ClientPortalRoute>
                <V2DefaultRedirect>
                  <Suspense fallback={<PageLoader />}>
                    <ClientPortalRouter />
                  </Suspense>
                </V2DefaultRedirect>
              </ClientPortalRoute>
            } />

            {/* ============================================ */}
            {/* V1 partner portal — deleted in K4 (2026-05-10).  */}
            {/* Catch-all redirects to the V2 surface so legacy  */}
            {/* bookmarks and external links keep working.       */}
            {/* V2DefaultRedirect picks the role-correct target  */}
            {/* (partner → /v2/partner-portal/:id). PageLoader   */}
            {/* only flashes while auth resolves.                */}
            {/* ============================================ */}
            <Route path="/portal/*" element={
              <PartnerRoute>
                <V2DefaultRedirect><PageLoader /></V2DefaultRedirect>
              </PartnerRoute>
            } />
            <Route path="/p/*" element={
              <PartnerRoute>
                <V2DefaultRedirect><PageLoader /></V2DefaultRedirect>
              </PartnerRoute>
            } />

            {/* ============================================ */}
            {/* v2 — clean rewrite under design system.       */}
            {/* V1 deprecation chunk K1: open beyond admin so */}
            {/* partners and client_users can land on their   */}
            {/* V2 surfaces. V2RouteGuard pins each role to   */}
            {/* the right scope; full V2 only for admins.     */}
            {/* The v2 app uses its own internal router so a  */}
            {/* single catch-all route is enough here.        */}
            {/* ============================================ */}
            <Route path="/v2/*" element={
              <V2RouteGuard>
                <Suspense fallback={<PageLoader />}>
                  <div className="v2-root">
                    <V2App />
                  </div>
                </Suspense>
              </V2RouteGuard>
            } />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
    </LanguageProvider>
    <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
